type QueueItem = {
  priority: number;
  start: () => void;
};

export const createConcurrencyManager = (maxConcurrency: number) => {
  if (maxConcurrency < 1) throw new Error('maxConcurrency must be ≥ 1');

  let activeCount = 0;
  let paused = false;
  const queue: QueueItem[] = [];

  const runNext = () => {
    if (paused || activeCount >= maxConcurrency) return;
    const next = queue.shift();
    if (!next) return;
    activeCount++;
    next.start();
  };

  const insertByPriority = (item: QueueItem) => {
    let insertIndex = queue.length;
    for (let i = 0; i < queue.length; i++) {
      if (queue[i].priority < item.priority) {
        insertIndex = i;
        break;
      }
    }
    queue.splice(insertIndex, 0, item);
  };

  const pause = () => {
    paused = true;
  };
  const resume = () => {
    if (!paused) return;
    paused = false;
    // Start processing queued items up to maxConcurrency
    while (activeCount < maxConcurrency && queue.length > 0) {
      runNext();
    }
  };

  const setActive = (active: boolean) => {
    if (active) {
      resume();
    } else {
      pause();
    }
  };

  return {
    setActive,
    pause,
    resume,
    enqueue: async <T>(taskFn: () => Promise<T>, priority = 0): Promise<T> =>
      new Promise<T>((resolve, reject) => {
        const start = () => {
          taskFn()
            .then(resolve, reject)
            .finally(() => {
              activeCount--;
              runNext();
            });
        };
        insertByPriority({ priority, start });
        runNext();
      }),
    get queueSize() {
      return queue.length;
    },
    get isPaused() {
      return paused;
    },
  };
};

export type ConcurrencyManager = ReturnType<typeof createConcurrencyManager>;
